import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import "./PortalLogin.css";
import smlLogoWhite from "../smart-lend-frontend/src/img/SML logo white.png";

const API_BASE = "https://www.smart-lend.com/api";

const signupInitialState = {
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  password: "",
};

const signinInitialState = {
  email: "",
  password: "",
};

const googleIcon = (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.002-.001 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const PortalLogin = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("signup");
  const [signupForm, setSignupForm] = useState(signupInitialState);
  const [signinForm, setSigninForm] = useState(signinInitialState);
  const [error, setError] = useState("");
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);
  const [isSigninSubmitting, setIsSigninSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const portalToken = params.get("portal_token");
    const portalName = params.get("portal_name");
    const view = params.get("view");
    const nextError = params.get("error");

    if (view === "signup" || view === "signin") {
      setActiveTab(view);
    }

    if (nextError) {
      setError(nextError);
      params.delete("error");
    }

    if (portalToken) {
      Cookies.set("bank_statement_portal_token", portalToken);
      Cookies.set("bank_statement_portal_name", portalName || "");

      params.delete("portal_token");
      params.delete("portal_name");
      params.delete("view");

      const queryString = params.toString();
      const cleanedUrl = `${window.location.pathname}${
        queryString ? `?${queryString}` : ""
      }${window.location.hash || ""}`;
      window.history.replaceState({}, document.title, cleanedUrl);
      history.replace("/bank-statement-portal");
      return;
    }

    if (Cookies.get("bank_statement_portal_token")) {
      history.replace("/bank-statement-portal");
      return;
    }

    params.delete("view");
    const queryString = params.toString();
    const cleanedUrl = `${window.location.pathname}${
      queryString ? `?${queryString}` : ""
    }${window.location.hash || ""}`;
    window.history.replaceState({}, document.title, cleanedUrl);
  }, [history]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setError("");
  };

  const handleSignupChange = (field) => (event) => {
    setSignupForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSigninChange = (field) => (event) => {
    setSigninForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const completePortalLogin = (json) => {
    Cookies.set("bank_statement_portal_token", json.token);
    Cookies.set("bank_statement_portal_name", json?.user?.name || "");
    history.push("/bank-statement-portal");
  };

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    setError("");
    setIsSignupSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/bank-statement-portal/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(signupForm),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json?.message || "Unable to create account.");
      }

      completePortalLogin(json);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSignupSubmitting(false);
    }
  };

  const handleSignin = async (event) => {
    event.preventDefault();
    setError("");
    setIsSigninSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/bank-statement-portal/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(signinForm),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json?.message || "Unable to sign in.");
      }

      completePortalLogin(json);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSigninSubmitting(false);
    }
  };

  const handleGoogleSignup = () => {
    const missingFields = ["first_name", "last_name", "company_name"].filter(
      (field) => !String(signupForm[field] || "").trim(),
    );

    if (missingFields.length > 0) {
      setError(
        "Please complete first name, last name, and company name before signing up with Google.",
      );
      return;
    }

    setError("");
    const params = new URLSearchParams({
      mode: "signup",
      first_name: signupForm.first_name.trim(),
      last_name: signupForm.last_name.trim(),
      company_name: signupForm.company_name.trim(),
    });

    window.location.assign(
      `${API_BASE}/bank-statement-portal/google/redirect?${params.toString()}`,
    );
  };

  const handleGoogleSignin = () => {
    setError("");
    window.location.assign(
      `${API_BASE}/bank-statement-portal/google/redirect?mode=signin`,
    );
  };

  const isSignupView = activeTab === "signup";

  return (
    <div className="portal-auth">
      <div className="wrap">
        <aside className="brand">
          <div className="brand-top">
            <a href="/home" className="brand-logo">
              <img src={smlLogoWhite} alt="SmartLend" className="brand-logo-img" />
            </a>
          </div>

          <div className="brand-mid">
            <h1>Start with 25 free tokens — on the house.</h1>
            <p>
              Create your account, drop in your first bank statement PDF, and
              see the full SmartLend Lender View in under 3 minutes. No credit
              card required.
            </p>
            <ul className="bullets">
              <li>25 free tokens credited instantly on sign-up</li>
              <li>25 tokens = 1 full multi-month bank statement</li>
              <li>Unrestricted Lender View &amp; PDF export</li>
              <li>Credits roll over when you upgrade</li>
            </ul>

            <div className="brand-card">
              <div className="tok">
                25
                <span>tokens</span>
              </div>
              <div>
                <h4>Your balance after sign-up</h4>
                <p>
                  Enough to analyse one bank account over 10 months — or
                  multiple shorter uploads.
                </p>
              </div>
            </div>
          </div>

          <div className="brand-bot">
            <span>SOC 2 Type II</span>
            <span>MAS-aligned</span>
            <span>AWS Singapore</span>
            <span>TLS 1.3 · AES-256</span>
          </div>
        </aside>

        <main className="panel">
          <div className="panel-top">
            <span>{isSignupView ? "Already a partner?" : "New to SmartLend?"}</span>
            <a
              href="/bank-statement-portal/login"
              onClick={(event) => {
                event.preventDefault();
                switchTab(isSignupView ? "signin" : "signup");
              }}
            >
              {isSignupView ? "Sign in" : "Create account"}
            </a>
          </div>

          <div className="form-box">
            <div className="tabs">
              <button
                type="button"
                className={isSignupView ? "active" : ""}
                onClick={() => switchTab("signup")}
              >
                Create account
              </button>
              <button
                type="button"
                className={!isSignupView ? "active" : ""}
                onClick={() => switchTab("signin")}
              >
                Sign in
              </button>
            </div>

            {isSignupView ? (
              <div>
                <h2>Create your SmartLend account.</h2>
                <p className="lead">
                  Get 25 free tokens credited instantly — no credit card
                  required.
                </p>

                <div className="reward-banner">
                  <div className="ic">10</div>
                  <div>
                    You'll receive <b>25 free tokens</b> on sign-up — enough
                    for one full bank statement analysis.
                  </div>
                </div>

                {error ? (
                  <div className="portal-error" role="alert">
                    {error}
                  </div>
                ) : null}

                <div className="social-row social-row--single">
                  <button className="social" type="button" onClick={handleGoogleSignup}>
                    {googleIcon}
                    Google
                  </button>
                </div>

                <div className="divider">or sign up with email</div>

                <form onSubmit={handleCreateAccount}>
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor="fname">First name</label>
                      <input
                        id="fname"
                        type="text"
                        placeholder="Liyana"
                        autoComplete="given-name"
                        value={signupForm.first_name}
                        onChange={handleSignupChange("first_name")}
                        required
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="lname">Last name</label>
                      <input
                        id="lname"
                        type="text"
                        placeholder="Tan"
                        autoComplete="family-name"
                        value={signupForm.last_name}
                        onChange={handleSignupChange("last_name")}
                        required
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="email">Work email</label>
                    <input
                      id="email"
                      type="email"
                      placeholder="you@lenderpartner.com"
                      autoComplete="email"
                      value={signupForm.email}
                      onChange={handleSignupChange("email")}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="company">Company name</label>
                    <input
                      id="company"
                      type="text"
                      placeholder="Atlas Credit Pte Ltd"
                      autoComplete="organization"
                      value={signupForm.company_name}
                      onChange={handleSignupChange("company_name")}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="pw">Password</label>
                    <input
                      id="pw"
                      type="password"
                      placeholder="At least 10 characters"
                      autoComplete="new-password"
                      minLength={10}
                      value={signupForm.password}
                      onChange={handleSignupChange("password")}
                      required
                    />
                  </div>
                  <button className="btn-submit" type="submit" disabled={isSignupSubmitting}>
                    {isSignupSubmitting
                      ? "Creating account..."
                      : "Create account \u2192 Get 25 tokens"}
                  </button>
                </form>

                <p className="fine">
                  By creating an account you agree to our{" "}
                  <a href="/terms-and-conditions">Terms</a> and{" "}
                  <a href="/privacy-policy">Privacy Policy</a>. SmartLend is
                  MAS-aligned and SOC 2 Type II certified.
                </p>
              </div>
            ) : (
              <div>
                <h2>Welcome back.</h2>
                <p className="lead">Sign in to your SmartLend Lender View.</p>

                {error ? (
                  <div className="portal-error" role="alert">
                    {error}
                  </div>
                ) : null}

                <div className="social-row social-row--single">
                  <button className="social" type="button" onClick={handleGoogleSignin}>
                    {googleIcon}
                    Google
                  </button>
                </div>

                <div className="divider">or sign in with email</div>

                <form onSubmit={handleSignin}>
                  <div className="field">
                    <label htmlFor="email2">Work email</label>
                    <input
                      id="email2"
                      type="email"
                      placeholder="you@lenderpartner.com"
                      autoComplete="email"
                      value={signinForm.email}
                      onChange={handleSigninChange("email")}
                      required
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="pw2">Password</label>
                    <input
                      id="pw2"
                      type="password"
                      placeholder="Your password"
                      autoComplete="current-password"
                      value={signinForm.password}
                      onChange={handleSigninChange("password")}
                      required
                    />
                  </div>
                  <div className="forgot">
                    <a href="mailto:hello@smartlend.io?subject=Portal%20password%20reset">
                      Forgot password?
                    </a>
                  </div>
                  <button className="btn-submit" type="submit" disabled={isSigninSubmitting}>
                    {isSigninSubmitting ? "Signing in..." : "Sign in \u2192"}
                  </button>
                </form>

                <p className="fine">
                  Need an account?{" "}
                  <a
                    href="/bank-statement-portal/login"
                    onClick={(event) => {
                      event.preventDefault();
                      switchTab("signup");
                    }}
                  >
                    Get 25 free tokens
                  </a>
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PortalLogin;
